import type { Organization } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { EditViewProps } from '../../utils/types.js';
import { formikCheck, issuesToFormik } from '../../utils/validation.js';
import type { Type } from './create.view.js';
import { CreateView, typeContract } from './create.view.js';

export const CreateOrganizationPage: FC<{ view?: FC<EditViewProps<Type>> }> = ({
  view: View = CreateView,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik<Type>({
    initialValues: {
      code: '',
      eccReplyCcEmail: '',
      email: '',
      emailFrom: '',
      emailSignOff: '',
      firstName: '',
      lastName: '',
      name: '',
      patrollerInstructions: '',
      patrolMeetUpInstructions: '',
      policeStation: '',
    },
    onSubmit: (values) => {
      const admin: Organization.Admin = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
      };
      const body: Organization.Create = {
        admin,
        code: values.code,
        eccReplyCcEmail: values.eccReplyCcEmail || undefined,
        emailFrom: values.emailFrom || undefined,
        emailSignOff: values.emailSignOff || undefined,
        name: values.name,
        patrollerInstructions: values.patrollerInstructions || undefined,
        patrolMeetUpInstructions: values.patrolMeetUpInstructions || undefined,
        policeStation: values.policeStation,
      };
      const url = '/api/v1/organization';
      return auth.axios
        .post<Organization.Type>(url, body)
        .then(({ data }) => {
          enqueueSnackbar('New organization saved', { variant: 'success' });
          navigate(`/organization/${data._id}`);
          return undefined;
        })
        .catch((error: AxiosError<{ issues: Issue[] }>) => {
          console.log(error);
          Sentry.captureException(error);
          if (error.response && error.response.status === 400) {
            if ('issues' in error.response.data) {
              const issues = error.response.data.issues.map((issue) => ({
                ...issue,
                path:
                  issue.path.length && issue.path[0] === 'admin'
                    ? issue.path.slice(1)
                    : issue.path,
              }));
              formik.setErrors(issuesToFormik(issues));
              return;
            }
          }
          enqueueSnackbar('Unable to create organization', {
            variant: 'error',
          });
        });
    },
    validate: (value) => formikCheck(typeContract, value),
  });

  useSetTitle('Create an Organization');

  return <View cancel={() => navigate(-1)} formik={formik} />;
};
